import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

/* ---------- helpers ---------- */
const dkey = (d) =>
  !d ? "" : typeof d === "string" ? d.slice(0, 10) : format(new Date(d), "yyyy-MM-dd");
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// prefer earliest clock_in and latest clock_out per (date, employee)
function mergeClockRow(acc, row) {
  acc.hours += n(row.total_hours_decimal);
  acc.delivery_fee += n(row.delivery_fee);
  acc.on_shift += n(row.on_shift_deliveries);
  acc.off_shift += n(row.off_shift_deliveries);
  acc.free_deliveries += n(row.free_deliveries);
  acc.break_minutes += n(row.break_minutes);

    if (row.notes) {
    acc.notes = acc.notes ? acc.notes + " | " + row.notes : row.notes;
  }

  // times as strings "HH:mm:ss" – pick min/max
  if (row.clock_in) {
    if (!acc.clock_in || String(row.clock_in) < String(acc.clock_in)) {
      acc.clock_in = row.clock_in;
    }
  }
  if (row.clock_out) {
    if (!acc.clock_out || String(row.clock_out) > String(acc.clock_out)) {
      acc.clock_out = row.clock_out;
    }
  }
}

/* ---------- table names (from your schema) ---------- */
const TBL = {
  CLOCKINS: "clockins",
  EXPENSES: "expenses",
};

export default function WagesReport({ fromDate, toDate, employee = "All", hourlyRate = 9.5, viewType = "daily" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fromDate && toDate) fetchData();
    // eslint-disable-next-line
  }, [fromDate, toDate, hourlyRate, employee, viewType]);

  async function fetchData() {
    setLoading(true);
    try {
      const [
        { data: clockins, error: ec },
        { data: expenses, error: ee },
      ] = await Promise.all([
        supabase
          .from(TBL.CLOCKINS)
          .select("*")
          .gte("date", fromDate)
          .lte("date", toDate),
        supabase
          .from(TBL.EXPENSES)
          .select("*")
          .eq("category", "Paid to Staff")
          .gte("date", fromDate)
          .lte("date", toDate),
      ]);

      if (ec || ee) {
        const msg = [ec, ee].filter(Boolean).map((e) => e.message).join(" · ");
        throw new Error(msg || "Fetch error");
      }

      // Filter data by employee (if not "All"), normalize for comparison
      const cFiltered =
        employee === "All"
          ? clockins || []
          : (clockins || []).filter(
              (c) =>
                (c.employee || "").trim().toLowerCase() === employee.trim().toLowerCase()
            );
      const eFiltered =
        employee === "All"
          ? expenses || []
          : (expenses || []).filter(
              (e) =>
                (e.staff_name || "").trim().toLowerCase() === employee.trim().toLowerCase()
            );

      // Key by (date, employee)
      const key = (date, employee) => `${dkey(date)}|${(employee || "").trim()}`;

      // Aggregate clockins per key
      const clkAgg = {};
      for (const c of cFiltered) {
        const k = key(c.date, c.employee);
        if (!clkAgg[k]) {
          clkAgg[k] = {
            date: dkey(c.date),
            employee: c.employee ? c.employee.trim() : "",
            hours: 0,
            delivery_fee: 0,
            on_shift: 0,
            off_shift: 0,
            free_deliveries: 0,
            break_minutes: 0,
            clock_in: null,
            clock_out: null, notes: "", 
          };
        }
        mergeClockRow(clkAgg[k], c);
      }

      // Aggregate "Paid to Staff" expenses per key (use staff_name)
      const paidMap = {};
      for (const e of eFiltered) {
        const staff = e.staff_name ? e.staff_name.trim() : "";
        const k = key(e.date, staff);
        paidMap[k] = (paidMap[k] || 0) + n(e.amount);
      }

      // Union keys from both sources so pure-expense or pure-clockin days still show
      const allKeys = new Set([...Object.keys(clkAgg), ...Object.keys(paidMap)]);
      let out = Array.from(allKeys).map((k) => {
        const ca = clkAgg[k] || {};
        const [dateStr, employeeName] = k.split("|");
        const hours = n(ca.hours);
        const gross = hours * hourlyRate;
        const fee = n(ca.delivery_fee);
        const paid = n(paidMap[k]);
        const netPay = gross + fee - paid;

        return {
          date: dateStr,
          employee: ca.employee || employeeName || "",
          hours,
          grossPay: gross,
          deliveryFee: fee,
          paidViaExpense: paid,
          netPay,
          onShift: n(ca.on_shift),
          offShift: n(ca.off_shift),
          freeDeliveries: n(ca.free_deliveries),
          breakMinutes: n(ca.break_minutes),
          clockIn: ca.clock_in || "",
          clockOut: ca.clock_out || "",
          hrRate: hourlyRate,
           notes: ca.notes || "",   // 🆕
        };
      });

      // Grouping
      if (viewType !== "daily") {
        const g = {};
        for (const r of out) {
          const bucket =
            viewType === "weekly"
              ? format(parseISO(r.date), "RRRR-'W'II")
              : r.date.slice(0, 7);
          const k = `${bucket}|${r.employee}`;
          if (!g[k]) {
            g[k] = { ...r, date: bucket };
            // Times don't make sense for grouped rows
            g[k].clockIn = "";
            g[k].clockOut = "";
          } else {
            g[k].hours += r.hours;
            g[k].grossPay += r.grossPay;
            g[k].deliveryFee += r.deliveryFee;
            g[k].paidViaExpense += r.paidViaExpense;
            g[k].netPay += r.netPay;
            g[k].onShift += r.onShift;
            g[k].offShift += r.offShift;
            g[k].freeDeliveries += r.freeDeliveries;
            g[k].breakMinutes += r.breakMinutes;
          }
          g[k].hrRate = hourlyRate;
        }
        out = Object.values(g);
      }

      // Sort by date then employee
      out.sort((a, b) => (a.date === b.date ? a.employee.localeCompare(b.employee) : a.date.localeCompare(b.date)));

      setRows(out);
    } catch (err) {
      console.error("WagesReport fetch error:", err);
      toast.error("Data fetch error: " + err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  // CSV
  const exportCSV = () => {
    if (!rows.length) return;
    const header = [
      "Date",
      "Employee",
      "Hours",
      "Gross Pay (£)",
      "Delivery Fee (£)",
      "Paid via Expense (£)",
      "Net Pay (£)",
      "On-shift Deliveries",
      "Off-shift Deliveries",
      "Free Deliveries",
      "Break (min)",
      "Clock In",
      "Clock Out",
      "Hourly Rate (£)",
    ];
    const lines = [
      header.join(","),
      ...rows.map((r) =>
        [
          r.date,
          r.employee,
          r.hours.toFixed(2),
          r.grossPay.toFixed(2),
          r.deliveryFee.toFixed(2),
          r.paidViaExpense.toFixed(2),
          r.netPay.toFixed(2),
          r.onShift,
          r.offShift,
          r.freeDeliveries,
          r.breakMinutes,
          r.clockIn,
          r.clockOut,
          r.hrRate.toFixed(2),
        ].join(",")
      ),
    ];
    saveAs(new Blob([lines.join("\n")], { type: "text/csv" }), `wages_${fromDate}_${toDate}.csv`);
  };

  // Totals (across visible rows)
  const totals = useMemo(() => {
    return rows.reduce(
      (a, r) => ({
        hours: a.hours + r.hours,
        gross: a.gross + r.grossPay,
        fee: a.fee + r.deliveryFee,
        paid: a.paid + r.paidViaExpense,
        net: a.net + r.netPay,
        onShift: a.onShift + r.onShift,
        offShift: a.offShift + r.offShift,
        freeDel: a.freeDel + r.freeDeliveries,
        breakMin: a.breakMin + r.breakMinutes,
      }),
      { hours: 0, gross: 0, fee: 0, paid: 0, net: 0, onShift: 0, offShift: 0, freeDel: 0, breakMin: 0 }
    );
  }, [rows]);

  // simple styles
  const thStyle = { background: "#F5F5F5", fontWeight: 700 };
  const money = (v) => (v < 0 ? <span style={{ color: "crimson" }}>{v.toFixed(2)}</span> : v.toFixed(2));

  return (
    <div>
      {/* No filter controls here! */}
      {loading ? (
        <p>Loading…</p>
      ) : !rows.length ? (
        <p>No data for the selected range.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Employee</th>
              <th style={thStyle}>Hours</th>
              <th style={thStyle}>Gross Pay (£)</th>
              <th style={thStyle}>Delivery Fee (£)</th>
              <th style={thStyle}>Paid via Expense (£)</th>
              <th style={thStyle}>Net Pay (£)</th>
              <th style={thStyle}>On-shift Deliveries</th>
              <th style={thStyle}>Off-shift Deliveries</th>
              <th style={thStyle}>Free Deliveries</th>
              <th style={thStyle}>Break (min)</th>
              <th style={thStyle}>Clock In</th>
              <th style={thStyle}>Clock Out</th>
              <th style={thStyle}>Hourly Rate (£)</th>
              <th style={thStyle}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.date}_${r.employee}_${i}`}>
                <td>{r.date}</td>
                <td>{r.employee}</td>
                <td>{r.hours.toFixed(2)}</td>
                <td>{money(r.grossPay)}</td>
                <td>{money(r.deliveryFee)}</td>
                <td>{money(r.paidViaExpense)}</td>
                <td style={{ fontWeight: 600, color: r.netPay < 0 ? "crimson" : "green" }}>
                  {r.netPay.toFixed(2)}
                </td>
                <td>{r.onShift}</td>
                <td>{r.offShift}</td>
                <td>{r.freeDeliveries}</td>
                <td>{r.breakMinutes}</td>
                <td>{r.clockIn || "-"}</td>
                <td>{r.clockOut || "-"}</td>
                <td>{r.hrRate.toFixed(2)}</td>
                <td>{r.notes || "-"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Totals</th>
              <th />
              <th>{totals.hours.toFixed(2)}</th>
              <th>{money(totals.gross)}</th>
              <th>{money(totals.fee)}</th>
              <th>{money(totals.paid)}</th>
              <th style={{ color: totals.net < 0 ? "crimson" : "green" }}>{totals.net.toFixed(2)}</th>
              <th>{totals.onShift}</th>
              <th>{totals.offShift}</th>
              <th>{totals.freeDel}</th>
              <th>{totals.breakMin}</th>
              <th />
              <th />
              <th />
            </tr>
          </tfoot>
        </table>
      )}
      <button onClick={exportCSV} disabled={!rows.length} style={{ marginTop: 16 }}>Export CSV</button>
    </div>
  );
}